import { motion } from 'framer-motion';
import { Code, Key, Zap, Shield, Book, ArrowRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';

const endpoints = [
  {
    method: 'POST',
    path: '/v1/chat',
    description: 'Send a message to an AI employee and receive a response',
  },
  {
    method: 'GET',
    path: '/v1/employees',
    description: 'List all available AI employees and their capabilities',
  },
  {
    method: 'GET',
    path: '/v1/conversations',
    description: 'Retrieve conversation history with pagination',
  },
  {
    method: 'POST',
    path: '/v1/tasks',
    description: 'Create a new automated task for an AI employee',
  },
  {
    method: 'GET',
    path: '/v1/tasks/:id',
    description: 'Get the status and results of a specific task',
  },
  {
    method: 'POST',
    path: '/v1/brain/upload',
    description: 'Upload documents to your Brain AI knowledge base',
  },
  {
    method: 'GET',
    path: '/v1/usage',
    description: 'Get your current credit usage and remaining balance',
  },
  {
    method: 'POST',
    path: '/v1/webhooks',
    description: 'Register webhooks for real-time event notifications',
  },
];

const codeExample = `import { SweetPilot } from '@flowai/sdk';

const client = new SweetPilot({
  apiKey: process.env.NOSLPS_API_KEY,
});

// Chat with an AI employee
const response = await client.chat.create({
  employee: 'aria',
  message: 'Create a viral tweet about our new product launch',
  conversationId: 'conv_123', // optional
});

console.log(response.message);
// "🚀 Big news! Our revolutionary new product is finally here...

// Create an automated task
const task = await client.tasks.create({
  employee: 'atlas',
  type: 'analysis',
  input: {
    dataUrl: 'https://example.com/sales-data.csv',
    prompt: 'Analyze Q4 sales trends and identify opportunities',
  },
  webhook: 'https://your-app.com/webhooks/tasks',
});`;

export default function API() {
  return (
    <StaticPageLayout
      title="API Documentation"
      subtitle="Build powerful integrations with the SweetPilot REST API."
    >
      {/* Quick Start */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Quick Start</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Key,
              title: '1. Get API Key',
              description: 'Generate an API key from your dashboard settings.',
            },
            {
              icon: Code,
              title: '2. Install SDK',
              description: 'npm install @flowai/sdk or use our REST API directly.',
            },
            {
              icon: Zap,
              title: '3. Make Requests',
              description: 'Start interacting with AI employees programmatically.',
            },
          ].map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                <step.icon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-gray-400 text-sm">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Authentication */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Authentication</h2>
        <div className="p-6 rounded-2xl bg-dark-800/50 border border-white/5">
          <p className="text-gray-300 mb-4">
            All API requests require authentication using a Bearer token in the Authorization
            header:
          </p>
          <div className="p-4 rounded-xl bg-dark-900 font-mono text-sm overflow-x-auto">
            <code className="text-gray-300">
              <span className="text-purple-400">Authorization:</span> Bearer YOUR_API_KEY
            </code>
          </div>
          <div className="mt-4 flex items-start gap-2 text-amber-400 text-sm">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Keep your API key secure. Never expose it in client-side code or public repositories.
            </span>
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Endpoints</h2>
        <div className="space-y-3">
          {endpoints.map((endpoint) => (
            <div
              key={endpoint.path}
              className="flex items-center gap-4 p-4 rounded-xl bg-dark-800/50 border border-white/5 hover:border-white/20 transition-all"
            >
              <span
                className={`px-2 py-1 rounded text-xs font-mono font-bold ${
                  endpoint.method === 'GET'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-blue-500/20 text-blue-400'
                }`}
              >
                {endpoint.method}
              </span>
              <code className="text-purple-400 font-mono text-sm">{endpoint.path}</code>
              <span className="flex-1 text-gray-400 text-sm">{endpoint.description}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Code Example */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Code Example</h2>
        <div className="rounded-2xl bg-dark-800/50 border border-white/5 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 bg-dark-900 border-b border-white/5">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            <span className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-4 text-sm text-gray-500">example.ts</span>
          </div>
          <pre className="p-4 overflow-x-auto text-sm">
            <code className="text-gray-300">{codeExample}</code>
          </pre>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Rate Limits</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Plan</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Requests/min</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Requests/day</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              <tr>
                <td className="py-3 px-4 text-white">Free</td>
                <td className="py-3 px-4 text-gray-300">10</td>
                <td className="py-3 px-4 text-gray-300">100</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-white">Pro</td>
                <td className="py-3 px-4 text-gray-300">60</td>
                <td className="py-3 px-4 text-gray-300">1,500</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-white">Professional</td>
                <td className="py-3 px-4 text-gray-300">300</td>
                <td className="py-3 px-4 text-gray-300">50,000</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-white">Enterprise</td>
                <td className="py-3 px-4 text-gray-300">Custom</td>
                <td className="py-3 px-4 text-gray-300">Unlimited</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Book className="w-6 h-6 text-purple-400" />
          <h2 className="text-2xl font-bold text-white">Full Documentation</h2>
        </div>
        <p className="text-gray-400 mb-6">
          Explore our complete API reference with detailed examples, error codes, and best
          practices.
        </p>
        <a
          href="https://docs.flowai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
        >
          View Full Docs <ArrowRight className="w-4 h-4" />
        </a>
      </section>
    </StaticPageLayout>
  );
}
